
import { Component, Vue } from "vue-property-decorator";
import { plainToInstance } from "class-transformer";

import { IRouteMeta } from "@/router/interface";
import unit_routes from "@/api/routes/units";

import TestVersion from "@/models/unit/unitable/test/version";
import UnitVersion, { EUnitableType } from "@/models/unit/unit_version";

import CTestingsChangeSettings from "./TestingChangeSettings.vue";
import CTestingsChangeQuestions from "./TestingChangeQuestions.vue";
import UnitUnit from "@/models/unit/unit";
import { EAdmissionPermission } from "@/models/admission/permission";

export interface IChangingTesting {
  id: string | null;
  index: number | null;
}

export interface IChangedTesting {
  id: string;
  name: string;
  index: number | null;
}

@Component({
  name: "CTestingsChange",
  components: {
    CTestingsChangeSettings,
    CTestingsChangeQuestions
  }
})
export default class CTestingsChange extends Vue {
  private watchers: Function[] = [];

  public testing_id: string | null = null;
  public testing_index: number | null = null;
  public testing: UnitVersion | null = null;

  public current_user: IJWTUser | null = this.$store.state.currentUser;

  public tabs = [
    {
      id: 0,
      value: "Настройки"
    },
    {
      id: 1,
      value: "Вопросы"
    }
  ];
  public current_tab_id: number = 0;

  public changeTab(tab_id: number) {
    this.current_tab_id = tab_id;
  }

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.changingTestingProgram,
        async (changingTestingProgram: IChangingTesting | null) => {
          if (changingTestingProgram != null) {
            this.testing_id = changingTestingProgram.id;
            this.testing_index = changingTestingProgram.index;

            await this.initTesting();

            this.showModal();
          } else {
            this.testing_id = null;
            this.testing_index = null;
            this.testing = null;
          }
        }
      )
    );
  }

  private async initTesting() {
    if (this.testing_id) {
      await this.loadTesting();
    } else {
      this.testing = new UnitVersion({
        version: 1,
        is_draft: false,
        unit: new UnitUnit({ is_template: true, is_archived: false }),
        unitable: new TestVersion(),
        unitable_type: EUnitableType["Test::Version"]
      });
    }
  }

  public showModal() {
    this.$modal.show("change_testing_modal");
  }

  public hideModal() {
    this.$modal.hide("change_testing_modal");

    this.$store.commit("changingTestingProgram", null);
  }

  public async saveTesting() {
    if (this.testing) {
      if (this.testing.id) {
        await this.updateTesting();
      } else {
        await this.createTesting();
      }
    } else {
      this.hideModal();
    }
  }

  private async loadTesting() {
    return this.$api
      .get(unit_routes.version(this.testing_id!), {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: UnitVersion }) => {
        this.testing = plainToInstance(UnitVersion, res);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  private async createTesting() {
    return this.$api
      .post(unit_routes.versions, this.testing, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: UnitVersion }) => {
        this.$store.commit("changedTestingProgram", {
          id: res.id,
          name: res.title,
          index: this.testing_index
        });

        this.$notify({
          group: "notifications",
          type: "success",
          text: "Изменения сохранены",
          speed: 500
        });

        this.hideModal();
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  private async updateTesting() {
    return this.$api
      .put(unit_routes.version(this.testing!.id), this.testing, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: UnitVersion }) => {
        this.$store.commit("changedTestingProgram", {
          id: res.id,
          name: res.title,
          index: this.testing_index
        });

        this.$notify({
          group: "notifications",
          type: "success",
          text: "Изменения сохранены",
          speed: 500
        });

        this.hideModal();
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  public allowWrite() {
    return (
      this.$api.hasWritePermissions(
        [
          EAdmissionPermission.TESTINGS,
          EAdmissionPermission.EDUCATION_PROGRAMS
        ],
        false
      ) && !this.testing?.unit?.is_archived
    );
  }
}
